import { useMutation } from '@tanstack/react-query'
import { cbomApi, sbomApi } from '@/api/clients/apiClient'
import { useOrgStore } from '@/stores/orgStore'
import { useAuthStore } from '@/stores/authStore'

interface ReportCardProps {
  title: string
  description: string
  icon: string
  format: string
  onDownload: () => Promise<void>
}

function ReportCard({ title, description, icon, format, onDownload }: ReportCardProps) {
  const mutation = useMutation({ mutationFn: onDownload })
  return (
    <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl shadow-ambient dark:shadow-none border border-border-light dark:border-border-dark p-6 flex flex-col gap-4">
      <div className="flex items-start gap-4">
        <div className="p-3 bg-primary/10 rounded-lg">
          <span className="material-icons text-primary text-2xl">{icon}</span>
        </div>
        <div className="flex-1">
          <h3 className="text-base font-semibold text-text-light dark:text-text-dark">{title}</h3>
          <p className="text-sm text-subtle-light dark:text-subtle-dark mt-1">{description}</p>
          <span className="inline-block mt-2 px-2 py-0.5 text-xs font-mono bg-background-light dark:bg-background-dark rounded text-subtle-light dark:text-subtle-dark">
            {format}
          </span>
        </div>
      </div>

      {mutation.isError && (
        <p className="text-sm text-error bg-error/10 dark:bg-error/15 p-3 rounded-xl">
          {(mutation.error as Error).message}
        </p>
      )}

      {mutation.isSuccess && (
        <p className="text-sm text-success bg-green-50 dark:bg-green-900/20 p-3 rounded-lg flex items-center gap-2">
          <span className="material-icons text-base">check_circle</span>
          Download started successfully
        </p>
      )}

      <button
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
        className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 mt-auto"
      >
        {mutation.isPending ? (
          <>
            <span className="material-icons text-base animate-spin">sync</span>
            Generating…
          </>
        ) : (
          <>
            <span className="material-icons text-base">download</span>
            Download
          </>
        )}
      </button>
    </div>
  )
}

export default function Reports() {
  const { currentOrgId } = useOrgStore()
  const orgName = useAuthStore((s) => s.orgName)

  return (
    <div className="space-y-6" id="reports-page">
      <div>
        <h1 className="text-2xl font-bold text-text-light dark:text-text-dark">Reports</h1>
        <p className="text-sm text-subtle-light dark:text-subtle-dark mt-0.5">
          Export cryptographic inventory and security findings for{' '}
          <span className="font-medium text-text-light dark:text-text-dark">
            {orgName ?? currentOrgId}
          </span>
        </p>
      </div>

      {/* Export reports */}
      <div>
        <h2 className="text-base font-semibold text-text-light dark:text-text-dark mb-4">
          Export Reports
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" id="report-cards">
          <ReportCard
            title="CycloneDX SBOM"
            description="Software Bill of Materials across all org sources in CycloneDX 1.7 format. Contains all discovered packages and libraries."
            icon="inventory_2"
            format="application/vnd.cyclonedx+json"
            onDownload={() => sbomApi.downloadOrgSbom(currentOrgId, 'cyclonedx')}
          />
          <ReportCard
            title="CycloneDX CBOM"
            description="Full Cryptographic Bill of Materials in CycloneDX 1.7 format. Contains all discovered algorithms, keys, certificates, and protocols."
            icon="inventory"
            format="application/vnd.cyclonedx+json"
            onDownload={() => cbomApi.downloadOrgCbom(currentOrgId)}
          />
          <ReportCard
            title="Security Findings"
            description="Prioritised list of security findings with risk levels, evidence, and remediation guidance for quantum-vulnerable assets."
            icon="security"
            format="application/json"
            onDownload={() => cbomApi.downloadOrgFindings(currentOrgId)}
          />
          <ReportCard
            title="SARIF 2.1.0"
            description="Static Analysis Results Interchange Format. Compatible with GitHub Advanced Security, Azure DevOps, and other CI/CD pipelines."
            icon="code"
            format="application/json (SARIF)"
            onDownload={() => cbomApi.downloadOrgSarif(currentOrgId)}
          />
        </div>
      </div>

      {/* Compliance info */}
      <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl shadow-ambient dark:shadow-none border border-border-light dark:border-border-dark p-6">
        <h2 className="text-base font-semibold text-text-light dark:text-text-dark mb-4">
          Compliance Standards
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            {
              name: 'MAS TRM 2021',
              desc: 'Monetary Authority of Singapore Technology Risk Management guidelines',
              icon: 'account_balance',
            },
            {
              name: 'NIST SP 800-53',
              desc: 'Post-Quantum Cryptography migration framework and algorithm standards',
              icon: 'flag',
            },
            {
              name: 'ISO 27001:2022',
              desc: 'Information security management systems — cryptographic controls',
              icon: 'verified',
            },
          ].map((std) => (
            <div
              key={std.name}
              className="flex items-start gap-3 p-4 bg-background-light dark:bg-background-dark rounded-lg"
            >
              <span className="material-icons text-primary text-xl mt-0.5">{std.icon}</span>
              <div>
                <p className="text-sm font-medium text-text-light dark:text-text-dark">{std.name}</p>
                <p className="text-xs text-subtle-light dark:text-subtle-dark mt-0.5">{std.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-subtle-light dark:text-subtle-dark mt-4">
          Compliance mapping is embedded in the Security Findings and CBOM exports.
        </p>
      </div>
    </div>
  )
}
